import { UnreachableError } from 'base/helpers';
import { AccessLevel, CommentThread } from 'blocks/blocks_service';
import { StringHashParameterStore } from 'blocks/hash_parameter';
import { computed, makeObservable, observable } from 'mobx';
import { EditorMode, editorModeStore } from '../editor_mode';
import { CommentAnchoringViewport } from '../model';
import { globalSelectionHoverRegistry } from '../selection/frame';
import { globalSelectionsMeasurer } from '../selection/measurer';
import { idForSelectionState, SelectionState } from '../selection/states';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { breakpointStore } from '../wireframe/breakpoint';

const goToCommentThreadParamStore = new StringHashParameterStore(
  'goToCommentThread',
);

export type ThreadFilter = 'active' | 'resolved';

export class CommentingUiStore {
  constructor(
    readonly session: ProjectSession,
    private readonly selection: SelectionStore,
  ) {
    makeObservable(this);

    const v = goToCommentThreadParamStore.get();
    if (v) {
      const threadId = parseInt(v);
      const thread = this.session.comments.getThread(threadId);
      if (!thread?.deleted && !isNaN(threadId)) {
        this.goToCommentThread(threadId);
      }
      goToCommentThreadParamStore.set(undefined);
    }
  }

  @observable.ref
  showCommentsPreference = true;

  get showComments() {
    return (
      this.showCommentsPreference ||
      this.addMode ||
      this.selectedThreadId != null
    );
  }

  /** If the user is in add-comment mode (all clicks are interpreted as adding a comment) */
  @observable.ref
  addMode = false;

  /** If the user clicked to add a thread at a specific location */
  @observable.ref
  addThreadAt:
    | {
        attachTo: {
          selection: SelectionState;
          x: number;
          y: number;
        }[];
        originalClientX: number;
        originalClientY: number;
      }
    | undefined = undefined;

  @observable.ref
  addModeFilter: ThreadFilter = 'active';

  @computed
  get filterFn(): (t: CommentThread) => boolean {
    const filter = this.addMode ? this.addModeFilter : 'active';
    switch (filter) {
      case 'active':
        return (t) => !t.resolved;
      case 'resolved':
        return (t) => t.resolved;
      default:
        throw new UnreachableError(filter);
    }
  }

  /** The ID of the thread card hovered in the sidebar */
  @observable.ref
  sidebarHoveringThreadId: number | undefined = undefined;

  /** The ID of the thread card hovered on the canvas */
  @observable.ref
  canvasHoveringThreadId: number | undefined = undefined;

  @observable.ref
  selectedThreadId: number | undefined = undefined;

  readonly comments = this.session.comments;

  /** Goes to a comment which might not be in the current viewport */
  goToCommentThread(threadId: number) {
    this.selectedThreadId = threadId;
    const selection = this.comments.getThreadSelectionAndAnchoring(threadId);
    if (selection == null) {
      return null;
    }

    switch (selection.anchoring.viewport) {
      case 'sitemap':
        editorModeStore.set(EditorMode.Sitemap);
        break;
      case 'wireframe-desktop':
        editorModeStore.set(EditorMode.Wireframe);
        breakpointStore.set('desktop');
        break;
      case 'wireframe-tablet':
        editorModeStore.set(EditorMode.Wireframe);
        breakpointStore.set('tablet');
        break;
      case 'wireframe-mobile':
        editorModeStore.set(EditorMode.Wireframe);
        breakpointStore.set('mobile');
        break;
      case 'styleguide':
        editorModeStore.set(EditorMode.Styleguide);
        break;
      default:
        throw new UnreachableError(selection.anchoring.viewport);
    }
    this.selection.set(selection.sel, { panMode: 'center' });
  }

  @computed get activeViewport(): CommentAnchoringViewport {
    const em = editorModeStore.get();
    switch (em) {
      case EditorMode.Sitemap:
        return 'sitemap';
      case EditorMode.Wireframe:
      case EditorMode.Design:
        return `wireframe-${breakpointStore.get()}`;
      case EditorMode.Styleguide:
        return 'styleguide';
      default:
        throw new UnreachableError(em);
    }
  }

  get loggedIn() {
    return this.session.InlineAuthDialog == null;
  }

  /** If they can comment _after they log in_ */
  get canComment() {
    return (
      this.session.productRestrictions.commentsEnabled &&
      this.session.accessLevel >= AccessLevel.COMMENTER
    );
  }
}

export function getAttachmentsAtCursor(clientX: number, clientY: number) {
  const selectable = globalSelectionHoverRegistry.getSelectableAtCursor(
    clientX,
    clientY,
  );
  if (!selectable) {
    return;
  }
  const attachTo: SelectionState[] = selectable.state.element
    ? // When commenting on a element, attach to both the node and section
      [
        selectable.state,
        { ...selectable.state, element: undefined, nodeId: undefined },
      ]
    : [selectable.state];
  return attachTo.map((selection) => {
    const id = idForSelectionState(selection);
    const el = globalSelectionsMeasurer.get('selectable', id).el;
    if (!el) {
      throw new Error('no element found for selectable ' + id);
    }
    const box = el.getBoundingClientRect();
    const x = (clientX - box.left) / box.width;
    const y = (clientY - box.top) / box.height;
    return { selection, x, y };
  });
}
